import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Typography from 'kingpin/atoms/Typography';
import MarkdownBody from 'components/MarkdownBody';
import Row from 'components/Common/Row';

import { AgentKbContext } from './AgentKbProvider';
import {
  ARTEFACT_KIND_HINTS,
  artefactDisplayName,
  artefactKindLabel,
} from './constants';

// One artefact, read-only, rendered as its markdown. The system (graphql-owned)
// knowledge base is authored in its own pipeline and rolled out to the bucket —
// this screen shows exactly what the agent received, its front matter and body,
// but never edits it.

const Pane = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  min-width: 0;
`;

const HeaderDiv = styled.div`
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--color-border-secondary);
`;

const FieldsDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const FieldDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 640px;
`;

const PreviewDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  min-height: 420px;
  min-width: 0;
  overflow-x: auto;
  font-size: 12px;
`;

const SourceBlock = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 12px;
  color: var(--color-text-primary);
`;

// The KB renamed `agent-context-name` → `domain-context-name`; both are live,
// because a pinned older version folder still carries the old one. Show the key
// the artefact actually has.
const contextNameKeyOf = (frontmatter: { [key: string]: string }): string =>
  'agent-context-name' in frontmatter && !('domain-context-name' in frontmatter)
    ? 'agent-context-name'
    : 'domain-context-name';

// Front-matter keys this screen surfaces per kind. Anything else on the artefact
// is left off the summary — the KB may carry keys this screen has never heard of.
const fieldsForKind = (
  kind: AgentKb.ArtefactKind,
  frontmatter: { [key: string]: string },
): string[] => {
  if (kind === 'workflowSkill') {
    return ['name', 'description', 'order'];
  }
  if (kind === 'domainSkill') {
    return ['name', 'description', contextNameKeyOf(frontmatter)];
  }
  if (kind === 'reference') {
    return ['name', 'description'];
  }
  return [];
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  order: 'Order (always-on position)',
  'agent-context-name': 'Domain context names (comma separated)',
  'domain-context-name': 'Domain context names (comma separated)',
};

const ArtefactEditor = ({ artefact }: { artefact: AgentKb.Artefact }) => {
  const { getRevisionBody } = useContext(AgentKbContext);
  const [body, setBody] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;
    setIsLoaded(false);
    getRevisionBody({ artefactId: artefact.id, rev: artefact.draftRev }).then(
      (loadedBody) => {
        if (!isActive) {
          return;
        }
        setBody(loadedBody ?? '');
        setIsLoaded(true);
      },
    );
    return () => {
      isActive = false;
    };
  }, [artefact.id, artefact.draftRev, getRevisionBody]);

  return (
    <Pane data-testid={`agent-kb-editor-${artefact.id}`}>
      <HeaderDiv>
        <Typography.Header type="H4">
          {artefactDisplayName(artefact)}
        </Typography.Header>
        <Row centerAlign style={{ gap: 8, marginTop: 8 }}>
          <Badge badgeType="Default" text={artefactKindLabel(artefact.kind)} />
          <Badge badgeType="Default" text="Read-only" />
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {artefact.path} · revision {artefact.draftRev}
          </Typography.Body>
        </Row>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {ARTEFACT_KIND_HINTS[artefact.kind]}
        </Typography.Body>
      </HeaderDiv>

      <FieldsDiv>
        {fieldsForKind(artefact.kind, artefact.frontmatter).map((key) => (
          <FieldDiv key={key}>
            <Typography.Body type="Label">
              {FIELD_LABELS[key] ?? key}
            </Typography.Body>
            <Typography.Body type="Body 13">
              {artefact.frontmatter[key] ?? '—'}
            </Typography.Body>
          </FieldDiv>
        ))}
      </FieldsDiv>

      {!isLoaded && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Loading…
        </Typography.Body>
      )}
      {isLoaded && (
        <PreviewDiv data-testid="agent-kb-body">
          {/* The semantic layer is YAML — rendering it as markdown would eat
              its indentation, which is the whole structure. */}
          {artefact.kind === 'semanticLayer' ? (
            <SourceBlock>{body}</SourceBlock>
          ) : (
            <MarkdownBody>{body}</MarkdownBody>
          )}
        </PreviewDiv>
      )}
    </Pane>
  );
};

export default ArtefactEditor;
